import * as React from "react";
import { useCallback, useContext, useEffect, useState } from "react";
import { styled } from "@mui/material";
import {
  PredefinedInsight,
  QuestionOptions,
} from "rag-chat/src/graphql/types/graphql";
import useSessionStore from "rag-chat/src/context/useSessionStore";
import { Answer } from "rag-chat/src/components/Answer";
import { ChatMessage, MessageType } from "rag-chat/src/context/sessionStore";
import { SkeletonLoader } from "../loader/skeletonLoader";
import { MessageList } from "@chatscope/chat-ui-kit-react";
import ChatContainer from "rag-chat/src/components/UI/ChatContainer";
import { WebsocketContext } from "rag-chat/src/context/WebsocketContext";

//region [[ Styles ]]

const PatientCardAnswerPromptView = styled(MessageList)({
  ".clinic-tab-panel & .scrollbar-container": {
    paddingTop: 12,
  },
});
const SkeletonLoaderView = styled("div")({
  ".clinic-tab-panel &": {
    paddingTop: 20,
  },
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface PatientCardAnswerPromptProps {
  insightType: PredefinedInsight;
}

const options: QuestionOptions = {
  guidelineId: null,
  forceGuidelines: false,
  disableGuidelines: false,
};

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const PatientCardAnswerPrompt = ({
  insightType,
}: PatientCardAnswerPromptProps) => {
  const {
    loadingRagSettings,
    loadingChatSession,
    ragSettings,
    replyIsLoading,
    askPromptInSession,
    sessionId,
    wsSessionId,
  } = useSessionStore();

  const { getStream } = useContext(WebsocketContext);

  // const {
  //   answer: streamingAnswer,
  //   streamId,
  //   isStreaming,
  //   guidelines: streamingGuidelines,
  //   altGuidelines: streamingAltGuidelines,
  // } = useStreamingAnswer(ragSettings.wssEndpoint, wsSessionId, insightType);

  //const streamIdRef = useRef(streamId);
  //const askPromptInSessionRef = useRef(true);

  const [answer, setAnswer] = useState<ChatMessage>(null);

  const customPrompt = null; //TODO: get custom prompt from clinic settings
  const customPromptName = null;

  const getGuidelineWithPrompt = useCallback(
    async (index: number, guidelineId: string): Promise<ChatMessage> => {
      setAnswer((prevState) => ({ ...prevState, text: "" })); // clear answer text guidelineId changes
      return askPromptInSession(
        options,
        insightType,
        customPrompt,
        customPromptName,
        guidelineId,
      );
    },
    [askPromptInSession, options, insightType],
  );

  useEffect(() => {
    setAnswer(null); // clear answer when insightType or case changes
  }, [sessionId, insightType]);

  useEffect(() => {
    if (replyIsLoading(insightType)) {
      const stream = getStream(wsSessionId, insightType);
      if (stream)
        setAnswer({
          index: 0,
          text: stream?.text,
          guidelines: stream?.metadata?.guidelines?.concat(
            stream?.metadata.altGuidelines,
          ),
          type: MessageType.answer,
        });
    }
  }, [wsSessionId, insightType, replyIsLoading, getStream]);

  useEffect(() => {
    (async () => {
      if (
        loadingRagSettings ||
        loadingChatSession ||
        replyIsLoading(insightType)
      )
        return;

      const answer = await askPromptInSession(options, insightType);
      setAnswer(answer);
    })();
  }, [
    loadingRagSettings,
    loadingChatSession,
    insightType,
    replyIsLoading,
    askPromptInSession,
  ]);

  if (loadingRagSettings || loadingChatSession || !answer)
    return (
      <SkeletonLoaderView>
        <SkeletonLoader />
      </SkeletonLoaderView>
    );

  return (
    <ChatContainer embedded={true} rtlDisplay={ragSettings.rtlDisplay}>
      <PatientCardAnswerPromptView id={"answer-prompt-container"}>
        <MessageList.Content>
          {answer && (
            <Answer
              message={answer}
              loading={replyIsLoading(insightType)}
              debugMode={ragSettings?.debugMode}
              standalone={true}
              usedPrompt={{
                promptName: customPromptName,
                predefinedInsight: insightType,
              }}
              getGuideline={getGuidelineWithPrompt}
            />
          )}
        </MessageList.Content>
      </PatientCardAnswerPromptView>
    </ChatContainer>
  );
};
